import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with base URL
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  maxRedirects: 5,  // Allow up to 5 redirects
  validateStatus: function (status) {
    return status >= 200 && status < 400; // Accept 3xx redirect status codes
  }
});

let subscriptionDialogHandler = null;
let authRefreshHandler = null;

// Function to set the subscription dialog handler
export const setSubscriptionDialogHandler = (handler) => {
  subscriptionDialogHandler = handler;
};

// Function to set the auth refresh handler
export const setAuthRefreshHandler = (handler) => {
  authRefreshHandler = handler;
};

// Create a map to store pending requests
const pendingRequests = new Map();

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry && authRefreshHandler) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const newToken = await authRefreshHandler();
        
        if (newToken) {
          // Update the failed request with the new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
      }

      // If we couldn't refresh the token, redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 402 Payment Required error
    if (error.response?.status === 402) {
      // Extract model name from error message if available
      const modelName = error.response.data?.detail?.match(/Model "(.*?)" requires/)?.[1] || '';
      
      // Show subscription dialog if handler is available
      if (subscriptionDialogHandler) {
        subscriptionDialogHandler(modelName);
      }
    }

    return Promise.reject(error);
  }
);

export const generateImage = async ({ prompt, model, dimension_preset, project_id }) => {
  try {
    const response = await api.post('/image/generate', { 
      prompt,
      model,
      dimension_preset,
      project_id
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getHistory = async (params) => {
  try {
    const response = await api.get('/image/history', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getArticles = async (projectId = null) => {
  try {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get('/articles', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getArticleDetail = async (articleId) => {
  try {
    const response = await api.get(`/articles/${articleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateArticle = async (articleId, data) => {
  try {
    const response = await api.put(`/articles/${articleId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createArticle = async (articleData) => {
  try {
    const response = await api.post('/articles/', articleData);
    return response.data;
  } catch (error) {
    console.error('Error creating article:', error);
    throw error;
  }
};

export const generateArticlePlan = async (articleId, model = null) => {
  try {
    const params = model ? { model } : {};
    const response = await api.get(
      `/articles/${articleId}/paragraphs/generate-plan`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const confirmArticlePlan = async (articleId, customPlan = null) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/confirm-plan`,
      customPlan ? { raw_plan: customPlan } : {}
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const writeParagraph = async (articleId, paragraphId, model = null) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/write`,
      model ? { model } : {}
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rewriteParagraph = async (articleId, paragraphId, options) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/rewrite`,
      options
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const writeAllParagraphs = async (articleId, model = null) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/write-all`,
      model ? { model } : {}
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateParagraph = async (articleId, paragraphId, data) => {
  try {
    console.log("Updating paragraph:", { articleId, paragraphId, data });
    const response = await api.put(
      `/articles/${articleId}/paragraphs/${paragraphId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error in updateParagraph:", error);
    throw error;
  }
};

export const generateImagePrompt = async (content, heading, projectId) => {
  try {
    // If content is an array, join it with newlines
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    
    const response = await api.post('/image/generate-prompt', {
      content: contentStr,
      heading
    }, {
      params: {
        project_id: projectId
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportArticle = async (articleId, format, options = {}) => {
  try {
    console.log(`Exporting article ${articleId} in format: ${format}`);
    
    // Determine MIME type based on format
    const mimeTypes = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      html: 'application/zip',
      md: 'application/zip',
      txt: 'text/plain',
      rtf: 'application/rtf',
      zip: 'application/zip'
    };
    
    const response = await api.post(
      `/articles/${articleId}/export`,
      {
        format,
        include_images: options.include_images ?? true,
        optimize_images: options.optimize_images ?? true,
        max_image_width: options.max_image_width ?? 1024
      },
      {
        responseType: 'blob',
        headers: {
          'Accept': mimeTypes[format] || '*/*'
        }
      }
    );
    
    console.log('Export response:', {
      status: response.status,
      headers: response.headers,
      size: response.data.size
    });

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers?.['content-disposition'];
    let filename = `article.${format === 'md' ? 'zip' : format}`;
    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/g.exec(contentDisposition);
      if (matches?.[1]) {
        filename = matches[1].replace(/['"]/g, '');
        console.log('Extracted filename:', filename);
      }
    }

    // Create a Blob with the correct MIME type
    const blob = new Blob([response.data], { type: mimeTypes[format] });
    
    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    console.log('File download initiated');
    return true;
  } catch (error) {
    console.error('Export error:', error);
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        headers: error.response.headers,
        data: error.response.data
      });
      
      // Try to read error message from blob if present
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          throw new Error(json.detail || 'Export failed');
        } catch (e) {
          throw new Error('Export failed: ' + error.response.statusText);
        }
      }
      
      throw new Error(error.response.data?.detail || 'Export failed');
    }
    throw error;
  }
};

export const uploadParagraphImage = async (articleId, paragraphId, file, projectId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/upload-image?project_id=${projectId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateArticleStatus = async (articleId, status) => {
  try {
    const response = await api.put(
      `/articles/${articleId}/status`,
      { status }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadArticleImage = async (articleId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/articles/${articleId}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportArticleBundle = async (articleId) => {
  try {
    const response = await api.get(
      `/articles/${articleId}/export-bundle`,
      {
        responseType: 'blob'  // Important for handling binary data
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'article-bundle.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch.length === 2) filename = filenameMatch[1];
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    throw error;
  }
};

export const uploadImage = async (formData, projectId) => {
  try {
    const response = await api.post(
      `/image/upload?project_id=${projectId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const attachImageToArticle = async (articleId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/attach-image`,
      { image_id: data.id }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const attachImageToParagraph = async (articleId, paragraphId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/attach-image`,
      { image_id: data.id }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addTagToArticles = async (articleIds, tag) => {
  try {
    const response = await api.post('/articles/add-tag', {
      article_ids: articleIds,
      tag
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const searchStockImages = async (query, source = 'all', projectId = null) => {
  try {
    const params = {
      query,
      source,
      page: 1,
      per_page: 20
    };
    
    if (projectId) {
      params.project_id = projectId;
    }
    
    const response = await api.get('/image-search/search', {
      params
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadFromUrl = async (data) => {
  try {
    const response = await api.post(
      '/image/upload-url',
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const attachContentImageToParagraph = async (articleId, paragraphId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/content-images`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const attachHeaderImage = async (articleId, paragraphId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/header-image`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjects = async () => {
  try {
    const response = await api.get('/projects/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createProject = async (data) => {
  try {
    const response = await api.post('/projects/', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteParagraph = async (articleId, paragraphId) => {
  try {
    const response = await api.delete(`/articles/${articleId}/paragraphs/${paragraphId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete paragraph');
  }
};

export const createParagraph = async (articleId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWebsiteImportJobs = async (projectId) => {
  try {
    const response = await api.get(`/website-imports/project/${projectId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createWebsiteImportJob = async (data) => {
  try {
    const response = await api.post('/website-imports/', {
      project_id: data.project_id,
      url: data.url,
      fetch_whole_domain: data.fetch_whole_domain,
      limit: data.limit,
      path_filter: data.path_filter,
      scrape_filter: data.scrape_filter
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const restartWebsiteImportJob = async (jobId) => {
  try {
    const response = await api.post(`/website-imports/${jobId}/restart`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to restart import job');
  }
};

export const updateWebsiteImportJob = async (jobId, data) => {
  try {
    const response = await api.put(`/website-imports/${jobId}`, {
      project_id: data.project_id,
      url: data.url,
      fetch_whole_domain: data.fetch_whole_domain,
      limit: data.limit,
      path_filter: data.path_filter,
      scrape_filter: data.scrape_filter
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getToneTraits = async () => {
  try {
    const response = await api.get('/brandvoice/traits');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectTones = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}/tones`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createToneOfVoice = async (projectId, data) => {
  try {
    const response = await api.post(`/projects/${projectId}/tones`, {
      name: data.name,
      description: data.description,
      custom_instructions: data.custom_instructions,
      personality_id: data.personality_id,
      selected_traits: data.selected_traits,
      is_default: data.is_default,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateToneOfVoice = async (projectId, toneId, data) => {
  try {
    const response = await api.put(`/projects/${projectId}/tones/${toneId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPersonalities = async () => {
  try {
    const response = await api.get('/brandvoice/personalities');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const listIdeas = async (projectId, postType = null, page = 1, size = 20) => {
  try {
    const params = {
      project_id: projectId,
      page,
      size
    };
    if (postType) {
      params.post_type = postType;
    }
    const response = await api.get('/ideas/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateIdea = async (ideaId, data) => {
  try {
    const response = await api.put(`/ideas/${ideaId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteIdea = async (ideaId) => {
  try {
    const response = await api.delete(`/ideas/${ideaId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateIdeas = async (data) => {
  try {
    const requestData = {
      ...data,
      topic: data.query
    };
    delete requestData.query;
    
    const response = await api.post('/ideas/generate', requestData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateInstantIdeas = async (projectId, topicPrefix) => {
  try {
    const response = await api.post('/ideas/instant', {
      project_id: projectId,
      topic_prefix: topicPrefix,
      post_type: 'article',
      limit: 3
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createFromIdea = async (ideaId, data) => {
  try {
    const response = await api.post(
      `/ideas/${ideaId}/create`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLLMModels = async () => {
  try {
    const response = await api.get('/models/llm');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectContents = async (projectId, page = 1, limit = 10) => {
  try {
    const response = await api.get(`/website-imports/content/project/${projectId}`, {
      params: {
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteContent = async (contentId) => {
  try {
    const response = await api.delete(`/website-imports/content/${contentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDashboardMetrics = async (projectId) => {
  try {
    const response = await api.get(`/dashboard/metrics/${projectId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/users/me');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Alias getCurrentUser as getMe for consistency with the TopBar component
export const getMe = getCurrentUser;

export const registerUser = async () => {
  try {
    const response = await api.post('/users/register');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getImageModels = async () => {
  try {
    const response = await api.get('/image/models');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectSettings = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/settings`);
  return response.data;
};

export const updateProjectSettings = async (projectId, settings) => {
  const response = await api.put(`/projects/${projectId}/settings`, settings);
  return response.data;
};

export const getSettingsConfig = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/settings/config`);
  return response.data;
};

// Add new resource management functions
export const getArticleResources = async (articleId) => {
  try {
    const response = await api.get(`/articles/${articleId}/resources`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const completeOnboarding = async (preferences = {}) => {
  try {
    const response = await api.post('/users/complete-onboarding', preferences);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePreferences = async (preferences) => {
  try {
    const response = await api.put('/users/preferences', preferences);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadArticleResource = async (articleId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/articles/${articleId}/resources`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addArticleResourceUrl = async (articleId, data) => {
  try {
    const formData = new FormData();
    formData.append('source_type', data.source_type);
    formData.append('source_url', data.source_url);

    const response = await api.post(
      `/articles/${articleId}/resources`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeArticleResource = async (articleId, resourceId) => {
  try {
    const response = await api.delete(`/articles/${articleId}/resources/${resourceId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveArticlePlan = async (articleId, rawPlan) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/save-plan`,
      { raw_plan: rawPlan }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadContent = async (file, projectId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/website-imports/content/upload?project_id=${projectId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const partialRewriteParagraph = async (articleId, paragraphId, data) => {
  try {
    const response = await api.post(
      `/articles/${articleId}/paragraphs/${paragraphId}/partial-rewrite`, 
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Subscription related endpoints
export const getSubscriptionTiers = async () => {
  const response = await api.get('/subscriptions/tiers');
  return response.data;
};

export const createCheckoutSession = async (data) => {
  const response = await api.post('/subscriptions/checkout', data);
  return response.data;
};

export const getCurrentSubscription = async () => {
  const response = await api.get('/subscriptions/current');
  return response.data;
};

export const createPortalSession = async (data) => {
  const response = await api.post('/subscriptions/portal', data);
  return response.data;
};


// New SSE endpoint for writing all paragraphs
export const writeAllParagraphsStream = (articleId, data) => {
  const url = `${API_BASE_URL}/articles/${articleId}/paragraphs/write-all`;
  const eventSource = new EventSource(url, {
    withCredentials: true, // Ensure cookies are sent if authentication is cookie-based
  });

  // Since SSE via Fetch is not straightforward, you might need to use a proxy or adjust the backend to support SSE over POST
  // Alternatively, implement using WebSockets for more flexibility

  return eventSource;
};

export const getLLMUsage = async () => {
  const response = await api.get('/usage/llm');
  return response.data;
};

export const getTransactions = async () => {
  const response = await api.get('/usage/transactions');
  return response.data;
};

export const deleteImage = async (imageId) => {
  try {
    const response = await api.delete(`/image/${imageId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getScheduledArticles = async (projectId) => {
  try {
    const params = projectId ? { project_id: parseInt(projectId, 10) } : {};
    const response = await api.get('/articles/scheduled', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Custom EventSource implementation that supports headers
class EventSourceWithAuth {
  constructor(url, options = {}) {
    this.url = url;
    this.options = options;
    this.eventSource = null;
    this.listeners = {
      message: [],
      error: [],
      open: []
    };
    this.lastProcessedLength = 0;
    
    this.connect();
  }

  connect() {
    // Create XHR for streaming
    const xhr = new XMLHttpRequest();
    xhr.open('GET', this.url, true);
    
    // Add headers
    if (this.options.headers) {
      Object.entries(this.options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }

    // Handle streaming response
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 3 || xhr.readyState === 4) {
        // Process only new chunks
        const chunk = xhr.responseText;
        if (chunk && chunk.length > this.lastProcessedLength) {
          // Get only the new part of the response
          const newChunk = chunk.slice(this.lastProcessedLength);
          this.lastProcessedLength = chunk.length;

          // Process new lines
          const lines = newChunk.split('\n');
          lines.forEach(line => {
            if (line.trim() && line.startsWith('data: ')) {
              const data = line.slice(6);
              this.listeners.message.forEach(listener => {
                listener({ data });
              });
            }
          });
        }
        
        // If the request is complete and we have a final chunk
        if (xhr.readyState === 4 && xhr.status === 200) {
          // Process any remaining data
          const remainingData = xhr.responseText.slice(this.lastProcessedLength);
          if (remainingData.trim()) {
            const lines = remainingData.split('\n');
            lines.forEach(line => {
              if (line.trim() && line.startsWith('data: ')) {
                const data = line.slice(6);
                this.listeners.message.forEach(listener => {
                  listener({ data });
                });
              }
            });
          }
        }
      }
    };

    // Handle errors
    xhr.onerror = () => {
      this.listeners.error.forEach(listener => {
        listener({ error: 'XHR Error' });
      });
    };

    // Start request
    xhr.send();
    this.xhr = xhr;
  }

  addEventListener(type, callback) {
    if (this.listeners[type]) {
      this.listeners[type].push(callback);
    }
  }

  removeEventListener(type, callback) {
    if (this.listeners[type]) {
      this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
    }
  }

  close() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }
}

export const writeParagraphStream = (articleId, paragraphId, model = null) => {
  const url = new URL(`${API_BASE_URL}/articles/${articleId}/paragraphs/${paragraphId}/write/stream`);
  
  if (model) {
    url.searchParams.append('model', model);
  }

  // Create custom EventSource with Bearer token in header
  const eventSource = new EventSourceWithAuth(url.toString(), {
    headers: {
      'Authorization': api.defaults.headers.common['Authorization']
    }
  });

  return {
    stream: eventSource,
    close: () => eventSource.close()
  };
};

export default api; 